import { createClient } from '@/lib/supabase/client';

export interface ProspectSubmission {
  id: string;
  companyName: string;
  individualName: string;
  title: string;
  phone: string;
  email: string;
  city: string;
  state: string;
  industry?: string;
  aiVisibilityScore: number | null;
  platformScores: Record<string, number>;
  competitors?: Record<string, number>;
  reportLink: string;
  submittedAt: string; // ISO string
  reportStatus: 'pending' | 'running' | 'complete' | 'error';
}

// ─── Row type matching Supabase DB schema ─────────────────────────────────────
interface ProspectRow {
  id: string;
  company_name: string;
  individual_name: string;
  title: string;
  phone: string;
  email: string;
  city: string;
  state: string;
  industry: string;
  ai_visibility_score: number | null;
  platform_scores: Record<string, number>;
  report_link: string;
  report_status: string;
  submitted_at: string;
}

function rowToProspect(row: ProspectRow): ProspectSubmission {
  return {
    id: row.id,
    companyName: row.company_name,
    individualName: row.individual_name,
    title: row.title,
    phone: row.phone,
    email: row.email,
    city: row.city,
    state: row.state,
    industry: row.industry,
    aiVisibilityScore: row.ai_visibility_score,
    platformScores: row.platform_scores ?? {},
    reportLink: row.report_link,
    submittedAt: row.submitted_at,
    reportStatus: row.report_status as ProspectSubmission['reportStatus'],
  };
}

export async function getAllProspects(): Promise<ProspectSubmission[]> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('prospects')
      .select('*')
      .order('submitted_at', { ascending: false });

    if (error) {
      console.error('[prospectStorage] getAllProspects error:', error.message);
      return [];
    }
    return (data as ProspectRow[]).map(rowToProspect);
  } catch (err) {
    console.error('[prospectStorage] getAllProspects exception:', err);
    return [];
  }
}

export async function saveProspect(prospect: ProspectSubmission): Promise<void> {
  try {
    const supabase = createClient();
    const row = {
      id: prospect.id,
      company_name: prospect.companyName,
      individual_name: prospect.individualName,
      title: prospect.title,
      phone: prospect.phone,
      email: prospect.email,
      city: prospect.city,
      state: prospect.state,
      industry: prospect.industry ?? '',
      ai_visibility_score: prospect.aiVisibilityScore,
      platform_scores: prospect.platformScores,
      report_link: prospect.reportLink,
      report_status: prospect.reportStatus,
      submitted_at: prospect.submittedAt,
    };

    const { error } = await supabase
      .from('prospects')
      .upsert(row, { onConflict: 'id' });

    if (error) {
      console.error('[prospectStorage] saveProspect error:', error.message);
    }
  } catch (err) {
    console.error('[prospectStorage] saveProspect exception:', err);
  }
}

export async function getProspectById(id: string): Promise<ProspectSubmission | null> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('prospects')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error('[prospectStorage] getProspectById error:', error.message);
      return null;
    }
    return data ? rowToProspect(data as ProspectRow) : null;
  } catch (err) {
    console.error('[prospectStorage] getProspectById exception:', err);
    return null;
  }
}

export async function getProspectsThisWeek(): Promise<ProspectSubmission[]> {
  try {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - dayOfWeek);
    startOfWeek.setHours(0, 0, 0, 0);

    const supabase = createClient();
    const { data, error } = await supabase
      .from('prospects')
      .select('*')
      .gte('submitted_at', startOfWeek.toISOString())
      .order('submitted_at', { ascending: false });

    if (error) {
      console.error('[prospectStorage] getProspectsThisWeek error:', error.message);
      return [];
    }
    return (data as ProspectRow[]).map(rowToProspect);
  } catch (err) {
    console.error('[prospectStorage] getProspectsThisWeek exception:', err);
    return [];
  }
}

export function generateProspectId(): string {
  return `prospect_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}
